import * as RadixSelect from '@radix-ui/react-select';
import isNil from 'lodash/isNil';
import { forwardRef, useState } from 'react';
import FormLabel from '../form/internals/form-label';
import Hint from '../hint';
import SelectContent from './components/select-content';
import SelectTrigger from './components/select-trigger';
import type { SelectProps } from './select.types';

const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      'aria-label': ariaLabelProp,
      label,
      hint,
      id = 'select',
      onChange,
      value,
      options,
      contentProps,
      disabled = false,
      className,
      size = 'default',
      hasError = false,
      placeholder = 'Select...',
      customTrigger,
      customFindOptionLabel,
      defaultOpen = false,
      alwaysOpen = false,
      onBlur,
      onFocus,
      autoFocus = false,
      onOpenChange,
      ...rest
    }: SelectProps,
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    // Normalize value to always be a string (never undefined/null) to keep RadixSelect consistently controlled
    // Treat empty string, null, and undefined as "no value" (empty string will show placeholder)
    const normalizedValue = value === '' || isNil(value) ? '' : String(value);

    const ariaLabel = (() => {
      if (label) {
        // A visible label exists, so no aria-label is needed.
        return undefined;
      }
      if (ariaLabelProp === null) {
        // ariaLabelProp is explicitly null, so don't set aria-label.
        return undefined;
      }
      // Use ariaLabelProp if provided, otherwise use the label of the selected option, or the placeholder as a last resort.
      return ariaLabelProp || options.find(option => option.value === normalizedValue)?.label || placeholder;
    })();

    const findOptionLabel = (value: string) => {
      if (value === '') return '';
      const option = options.find(option => option.value === value);
      return option?.component ?? option?.label ?? '';
    };

    const handleOpenChange = (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);
    };

    const handleValueChange = (value: string) => {
      onChange(value);
    };

    const renderCustomTrigger = () => {
      if (typeof customTrigger === 'function') {
        return customTrigger({ isOpen, disabled });
      }
      return customTrigger;
    };

    return (
      <div className="flex flex-col gap-2">
        {label && (
          <FormLabel hasError={hasError} htmlFor={id} size={size}>
            {String(label)}
          </FormLabel>
        )}
        <RadixSelect.Root
          value={normalizedValue}
          onValueChange={handleValueChange}
          onOpenChange={handleOpenChange}
          open={alwaysOpen || isOpen}
          disabled={disabled}
        >
          <SelectTrigger
            {...rest}
            ref={ref}
            value={normalizedValue}
            placeholder={placeholder}
            disabled={disabled}
            hasError={hasError}
            size={size}
            className={className}
            ariaLabel={ariaLabel}
            customTrigger={renderCustomTrigger()}
            findOptionLabel={customFindOptionLabel ?? findOptionLabel}
            id={id}
            onBlur={onBlur}
            onFocus={onFocus}
            autoFocus={autoFocus}
          />
          <RadixSelect.Portal>
            <SelectContent
              options={options}
              value={normalizedValue}
              size={size}
              ariaLabel={ariaLabel}
              contentProps={contentProps}
            />
          </RadixSelect.Portal>
        </RadixSelect.Root>
        {hint && (
          <Hint className="m-0!" hasError={hasError}>
            {hint}
          </Hint>
        )}
      </div>
    );
  },
);

export default Select;
